import { useEffect, useState } from 'react';

/**
 * useConsent — SSR-safe consent switchboard for analytics/marketing.
 *
 * WHAT
 *   Exposes { analytics, marketing, ready } so the app can decide whether to
 *   render non-essential tags (e.g., GTM, Klaviyo). Starts conservative during SSR.
 *
 * HOW
 *   - Reads a region policy bucket (`regionBucket` cookie or `initialBucket`):
 *       • REG_OPT_IN      → default DENY until explicit accept (EU/UK/EEA, etc.)
 *       • REG_OPT_OUT     → default ALLOW; must honor opt-out & GPC (some US states)
 *       • REST_OF_WORLD   → fallback policy (you control defaults)
 *   - Honors browser signals:
 *       • GPC (Global Privacy Control) → marketing = false
 *       • DNT (Do Not Track)           → analytics = false
 *   - Listens for `ketch:consent:update` and overrides with CMP decisions if/when present.
 *   - Sets `ready` once a client-side decision has been made (prevents SSR access to window).
 *
 * WHY (performance + compliance)
 *   - Prevents heavy third-party scripts from running on the critical path or for users
 *     who shouldn’t receive them → better LCP/TBT and fewer unnecessary requests.
 *   - Still works if Ketch is blocked or unused; Ketch can upgrade state later.
 *
 * USAGE
 *   const { analytics, marketing, ready } = useConsent(bucketFromMiddleware);
 *   {ready && analytics && <GoogleTagManager gtmId={GTM_ID} />}
 *   {ready && marketing && <AfterLCP><KlaviyoScript /></AfterLCP>}
 *
 * NOTES
 *   - `regionBucket` at the edge (middleware) for accurate first-paint defaults.
 *   - Buckets represent regional policy, not an individual’s choice.
 */

type Consent = { analytics: boolean; marketing: boolean; ready: boolean };

const defaultsFor = (bucket: string, gpc: boolean, dnt: boolean): Consent => {
  const marketingAllowed = !gpc; // honor GPC globally
  const analyticsAllowed = !dnt; // honor Do Not Track
  if (bucket === 'REG_OPT_IN') return { analytics: false, marketing: false, ready: true };
  if (bucket === 'REG_OPT_OUT')
    return { analytics: analyticsAllowed, marketing: marketingAllowed, ready: true };
  return { analytics: analyticsAllowed, marketing: marketingAllowed, ready: true }; // REST_OF_WORLD
};
// Buckets vs. user choice
// REG_OPT_IN (EU/UK/EEA, etc.) → default deny until the user explicitly accepts.
// REG_OPT_OUT (some US states) → default allow; must provide a “Do Not Sell/Share” control and honor GPC.
// REST_OF_WORLD → your fallback policy (you decide defaults).
export function useConsent(initialBucket?: string) {
  // SSR-safe baseline: block non-essential until we know more
  const [state, setState] = useState<Consent>({ analytics: false, marketing: false, ready: false });

  useEffect(() => {
    const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

    const bucket =
      initialBucket ??
      (isBrowser
        ? document.cookie.match(/(?:^|;)\s*regionBucket=([^;]+)/)?.[1] ?? 'REST_OF_WORLD'
        : 'REST_OF_WORLD');

    const gpc = isBrowser && (window as any).navigator?.globalPrivacyControl === true;
    const dnt =
      isBrowser && ((navigator as any).doNotTrack === '1' || (window as any).doNotTrack === '1');

    setState(defaultsFor(bucket, !!gpc, !!dnt));

    // If/when Ketch fires, let it override
    const handler = (e: any) => {
      const c = e?.detail;
      setState((s) => ({
        ...s,
        analytics: !!c?.analytics?.granted,
        marketing: !!c?.marketing?.granted,
        ready: true
      }));
    };

    if (isBrowser) window.addEventListener('ketch:consent:update', handler);
    return () => {
      if (isBrowser) window.removeEventListener('ketch:consent:update', handler);
    };
  }, [initialBucket]);

  return state; // { analytics, marketing, ready }
}
